/**
 * BW Trailer Hitches - Hitch Finder
 * Cascading vehicle select using Storefront API (no embedded JSON)
 *
 * Builds the same data shape as the Liquid JSON:
 * {
 *   products: [{
 *     handle, title, product_type,
 *     fitments: [{ id, make, year, model, body_style, bed_size }]
 *   }]
 * }
 */

class HitchFinder {
  constructor() {
    this.products = [];
    this.currentSelection = {
      product_type: null,
      make: null,
      year: null,
      model: null,
      body_style: null,
      bed_size: null
    };
    this.dataReady = false;
    this.pendingSelection = null; // Stores selection made before data loads

    this.fields = ['product_type', 'make', 'year', 'model', 'body_style', 'bed_size'];
    this.fieldLabels = {
      product_type: 'Product Type',
      make: 'Make',
      year: 'Year',
      model: 'Model',
      body_style: 'Body Style',
      bed_size: 'Detail'
    };

    // Read config from the section element (add these data-* attrs in Liquid)
    const root =
      document.querySelector('section.section--hitch-finder[data-storefront-token]') ||
      document.querySelector('.section--hitch-finder[data-storefront-token]');

    this.cfg = {
      shopDomain: root?.dataset.shopDomain || window.location.host,
      apiVersion: root?.dataset.apiVersion || '2025-01',
      collectionHandle: root?.dataset.collectionHandle || 'hitch-kits',
      token: root?.dataset.storefrontToken || ''
    };

    this.init();
  }

  async init() {
    try {
      // Bind events immediately (product type is server-side rendered)
      this.bindEvents();

      // Check if product types already rendered server-side
      const productTypeSelect = document.querySelector('[data-field="product_type"]');
      const hasServerOptions = productTypeSelect && productTypeSelect.options.length > 1;

      // Load full data (needed for subsequent dropdowns)
      await this.loadData();

      if (this.products.length > 0) {
        // Only repopulate if not server-rendered (fallback)
        if (!hasServerOptions) {
          this.populateProductTypes();
        }
        this.dataReady = true;

        // BW-CUSTOM: Log all fitments before any selection (for debugging missing data)
        // const allFitments = this.getAllFitments();
        // console.log('[HitchFinder] Loaded products:', this.products.length);
        // console.log('[HitchFinder] All fitments (before selection):', allFitments.length, allFitments);

        // Replay any selection made while data was loading
        if (this.pendingSelection) {
          const { field, value, levelIndex } = this.pendingSelection;
          this.pendingSelection = null;
          this.onSelectChange(field, value, levelIndex);
        }
      }
    } catch (error) {
      console.error('[HitchFinder] Init failed:', error);
    }
  }

  /**
   * Storefront helpers
   */
  async storefrontRequest(query, variables) {
    const { shopDomain, apiVersion, token } = this.cfg;

    if (!token) {
      throw new Error('Missing Storefront token (data-storefront-token)');
    }

    const endpoint = `https://${shopDomain}/api/${apiVersion}/graphql.json`;

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query, variables }),
    });

    const json = await res.json();

    if (json.errors?.length) {
      console.error('[HitchFinder] Storefront errors:', json.errors);
      throw new Error(json.errors[0]?.message || 'Storefront API error');
    }

    return json.data;
  }

  normalizeMetafieldValue(raw) {
    // Handles list values that may come back as JSON string arrays
    if (raw == null) return '';
    if (typeof raw !== 'string') return String(raw);
    const t = raw.trim();
    if (!t) return '';
    if (t.startsWith('[') && t.endsWith(']')) {
      try {
        const arr = JSON.parse(t);
        if (Array.isArray(arr) && arr.length) return String(arr[0]);
      } catch {}
    }
    return t;
  }

  labelFromMetaobject(fields) {
    const byKey = (k) => fields?.find(f => f.key === k)?.value;
    return (
      byKey('value') ||
      byKey('name') ||
      byKey('title') ||
      byKey('label') ||
      byKey('handle') ||
      ''
    );
  }

  resolveProductType(mf) {
    if (!mf) return '';
    const v = this.normalizeMetafieldValue(mf.value);
    if (v) return v;

    const ref = mf.references?.nodes?.[0];
    if (ref?.fields?.length) return this.labelFromMetaobject(ref.fields);

    return '';
  }

  /**
   * NEW: load data via Storefront API instead of parsing #hitch-fitment-data
   * Produces the exact same this.products shape your old Liquid JSON used.
   */
  async loadData() {
    // Basic config sanity
    if (!this.cfg.token) {
      console.error('[HitchFinder] Missing Storefront config on section. Add data-storefront-token.');
      return;
    }

    // BW-CUSTOM: Max references per request (Storefront API often allows 250)
    const REFS_PAGE_SIZE = 250;

    const collectionQuery = `
      query HitchFitment($handle: String!, $first: Int!, $after: String, $refsFirst: Int!) {
        collectionByHandle(handle: $handle) {
          products(first: $first, after: $after) {
            pageInfo { hasNextPage endCursor }
            nodes {
              id
              handle
              title

              product_type: metafield(namespace: "custom", key: "product_type") {
                value
                references(first: 5) {
                  nodes {
                    ... on Metaobject { fields { key value } }
                  }
                }
              }

              fits_vehicles: metafield(namespace: "custom", key: "fits_vehicles") {
                references(first: $refsFirst) {
                  pageInfo { hasNextPage endCursor }
                  nodes {
                    ... on Metaobject {
                      id
                      make: field(key: "make") { value }
                      year: field(key: "year") { value }
                      model: field(key: "model") { value }
                      body_style: field(key: "body_style") { value }
                      bed_size: field(key: "bed_size") { value }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const productRefsQuery = `
      query ProductFitmentRefs($id: ID!, $refsFirst: Int!, $refsAfter: String) {
        product(id: $id) {
          fits_vehicles: metafield(namespace: "custom", key: "fits_vehicles") {
            references(first: $refsFirst, after: $refsAfter) {
              pageInfo { hasNextPage endCursor }
              nodes {
                ... on Metaobject {
                  id
                  make: field(key: "make") { value }
                  year: field(key: "year") { value }
                  model: field(key: "model") { value }
                  body_style: field(key: "body_style") { value }
                  bed_size: field(key: "bed_size") { value }
                }
              }
            }
          }
        }
      }
    `;

    const mapRefsToFitments = (nodes) =>
      (nodes || []).map(f => ({
        id: null,
        make: f.make?.value || '',
        year: f.year?.value || '',
        model: f.model?.value || '',
        body_style: f.body_style?.value || '',
        bed_size: f.bed_size?.value || ''
      }));

    const PAGE_SIZE = 50;
    let after = null;
    let hasNextPage = true;
    const productsOut = [];

    while (hasNextPage && productsOut.length < 1000) {
      const data = await this.storefrontRequest(collectionQuery, {
        handle: this.cfg.collectionHandle,
        first: PAGE_SIZE,
        after,
        refsFirst: REFS_PAGE_SIZE
      });

      const conn = data?.collectionByHandle?.products;
      if (!conn) {
        break;
      }

      const nodes = conn.nodes || [];

      for (const p of nodes) {
        const productType = this.resolveProductType(p.product_type);
        const refsConn = p.fits_vehicles?.references;
        let fitRefs = refsConn?.nodes || [];
        let refsPageInfo = refsConn?.pageInfo || {};
        let refsAfter = refsPageInfo.endCursor || null;

        // BW-CUSTOM: Paginate references so we get all fitments (was capped at 50, now 250 + follow-up pages)
        while (refsPageInfo.hasNextPage && refsAfter) {
          const refsData = await this.storefrontRequest(productRefsQuery, {
            id: p.id,
            refsFirst: REFS_PAGE_SIZE,
            refsAfter
          });
          const nextRefs = refsData?.product?.fits_vehicles?.references;
          const nextNodes = nextRefs?.nodes || [];
          fitRefs = fitRefs.concat(nextNodes);
          refsPageInfo = nextRefs?.pageInfo || {};
          refsAfter = refsPageInfo.endCursor || null;
        }

        if (!productType || !fitRefs.length) continue;

        const productObj = {
          handle: p.handle,
          title: p.title,
          product_type: productType,
          fitments: mapRefsToFitments(fitRefs)
        };

        productsOut.push(productObj);

        if (productsOut.length >= 1000) break;
      }

      hasNextPage = !!conn.pageInfo?.hasNextPage;
      after = conn.pageInfo?.endCursor || null;
    }

    this.products = productsOut;
  }

  bindEvents() {
    this.fields.forEach((field, index) => {
      const select = document.querySelector(`[data-field="${field}"]`);
      if (select) {
        select.addEventListener('change', (e) => this.onSelectChange(field, e.target.value, index));
      }
    });

    document.getElementById('find-hitch-btn')?.addEventListener('click', () => this.findHitch());
  }

  populateProductTypes() {
    const productTypes = [...new Set(this.products.map(p => p.product_type).filter(Boolean))].sort();
    this.populateSelect('product_type', productTypes);
  }

  getAllFitments() {
    const fitments = [];
    this.products.forEach(product => {
      product.fitments.forEach(fitment => {
        fitments.push({
          ...fitment,
          product_type: product.product_type,
          productHandle: product.handle,
          productTitle: product.title
        });
      });
    });
    return fitments;
  }

  onSelectChange(field, value, levelIndex) {
    // If data not ready yet, queue this selection for replay
    if (!this.dataReady) {
      if (value) {
        this.pendingSelection = { field, value, levelIndex };
      } else {
        // User cleared selection - cancel any pending
        this.pendingSelection = null;
      }
      return;
    }

    this.currentSelection[field] = value || null;

    for (let i = levelIndex + 1; i < this.fields.length; i++) {
      this.currentSelection[this.fields[i]] = null;
      this.hideLevel(i);
    }

    document.querySelector('.submit-wrapper').style.display = 'none';
    document.getElementById('hitch-no-results').style.display = 'none';
    document.querySelector('.hitch-results')?.remove();

    if (!value) return;

    const filtered = this.getFilteredFitments();
    const nextFieldIndex = this.findNextField(levelIndex, filtered);

    if (nextFieldIndex !== -1) {
      const nextField = this.fields[nextFieldIndex];
      const options = [...new Set(filtered.map(f => f[nextField]).filter(v => v && v.trim() !== ''))];

      if (nextField === 'year') {
        options.sort((a, b) => parseInt(b) - parseInt(a));
      } else {
        options.sort();
      }

      this.populateSelect(nextField, options);

      // BW-CUSTOM: Skip single-option dropdowns entirely — no need to present a "choice" of one
      // Always show Model regardless of option count
      if (options.length === 1 && nextField !== 'model') {
        this.currentSelection[nextField] = options[0];
        const select = document.querySelector(`[data-field="${nextField}"]`);
        if (select) select.value = options[0];
        // Don't show the wrapper — recurse to find the next real choice
        const nextFiltered = this.getFilteredFitments();
        const followingIndex = this.findNextField(nextFieldIndex, nextFiltered);

        if (followingIndex !== -1) {
          this.onSelectChange(nextField, options[0], nextFieldIndex);
        } else {
          // Last field — show submit
          console.log('[HitchFinder] Final match:', this.currentSelection, '→', nextFiltered.length, 'fitment(s)');
          document.querySelector('.submit-wrapper').style.display = 'block';
        }
      } else {
        this.showLevel(nextFieldIndex);
      }
    } else {
      // Final match: show submit button (keep single log for debugging if needed)
      console.log('[HitchFinder] Final match:', this.currentSelection, '→', filtered.length, 'fitment(s)');
      document.querySelector('.submit-wrapper').style.display = 'block';
    }
  }

  findNextField(currentIndex, fitments) {
    for (let i = currentIndex + 1; i < this.fields.length; i++) {
      const field = this.fields[i];
      const values = [...new Set(fitments.map(f => f[field]).filter(v => v && v.trim() !== ''))];

      // Show select if 1 or more options; only skip when zero options
      if (values.length >= 1) {
        return i;
      }
    }
    return -1;
  }

  getFilteredFitments() {
    return this.getAllFitments().filter(f => {
      for (const field of this.fields) {
        const selectedValue = this.currentSelection[field];
        const fitmentValue = f[field];
        
        // Only compare if BOTH user has selected AND fitment has a value for this field
        if (selectedValue && fitmentValue && fitmentValue.trim() !== '') {
          if (fitmentValue !== selectedValue) {
            return false;
          }
        }
        // If either is empty, ignore this field in the comparison
      }
      return true;
    });
  }

  populateSelect(field, options) {
    const select = document.querySelector(`[data-field="${field}"]`);
    if (!select) return;

    select.innerHTML = `<option value="">- Select ${this.fieldLabels[field]} -</option>`;
    
    options.forEach(opt => {
      const option = document.createElement('option');
      option.value = opt;
      option.textContent = opt;
      select.appendChild(option);
    });
    select.disabled = false;
  }

  showLevel(index) {
    const wrapper = document.querySelector(`[data-level="${index}"]`);
    if (wrapper) {
      wrapper.style.display = 'block';
      wrapper.classList.add('fade-in');
    }
  }

  hideLevel(index) {
    const wrapper = document.querySelector(`[data-level="${index}"]`);
    const select = wrapper?.querySelector('select');
    if (wrapper) wrapper.style.display = 'none';
    if (select) {
      select.innerHTML = `<option value="">- Select ${this.fieldLabels[this.fields[index]]} -</option>`;
      select.disabled = true;
    }
  }

  findHitch() {
    const filtered = this.getFilteredFitments();

    if (filtered.length === 0) {
      this.showNoResults();
      return;
    }

    const productHandles = [...new Set(filtered.map(f => f.productHandle))];
    const matchingProducts = productHandles.map(handle => {
      const fitment = filtered.find(f => f.productHandle === handle);
      return { handle, title: fitment.productTitle };
    });

    if (matchingProducts.length === 0) {
      this.showNoResults();
    } else if (matchingProducts.length === 1) {
      window.location.href = `/products/${matchingProducts[0].handle}`;
    } else {
      this.showResults(matchingProducts);
    }
  }

  showResults(products) {
    document.getElementById('hitch-no-results').style.display = 'none';
    document.querySelector('.hitch-results')?.remove();

    const resultsEl = document.createElement('div');
    resultsEl.className = 'hitch-results';
    resultsEl.innerHTML = `
      <h4>Multiple Hitch Kits Found</h4>
      <div class="hitch-results-list">
        ${products.map(p => `
          <a href="/products/${p.handle}" class="hitch-result-item btn btn-primary">
            ${p.title}
          </a>
        `).join('')}
      </div>
    `;

    document.querySelector('.hitch-finder-form').after(resultsEl);
  }

  showNoResults() {
    document.querySelector('.hitch-results')?.remove();
    document.getElementById('hitch-no-results').style.display = 'block';
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.section--hitch-finder')) {
    new HitchFinder();
  }
});
